import { create } from 'zustand';

interface BrowserState {
  panelOpen: boolean;
  connected: boolean;
  /** Sidecar CDP reachability, streamed independently of the panel WS. */
  harnessConnected: boolean;
  zoomSignedIn: boolean;
  currentUrl: string;
  /** One-shot URL the panel should navigate to as soon as the harness
   *  connects (e.g. the Zoom portal when guided setup opens the browser).
   *  Cleared by BrowserPanel once the navigate is sent. */
  pendingNavigateUrl: string | null;
  /** When true, the browser viewport is blurred and non-interactive — used
   *  to gate the user on a prior step (e.g. picking an access level during
   *  guided Zoom setup) before they can use the browser. */
  disabled: boolean;
  /** When true, ZAC is actively driving the harness (e.g. provisioning the S2S
   *  app). Shows a blue "hands-off" overlay and blocks user manipulation. */
  agentControl: boolean;
  /** When true, the user has manually seized control of the browser ("Take
   *  over"). Overrides agentControl auto-toggles — ZAC pauses and the overlay
   *  drops until the user presses "Let ZAC continue". */
  userPaused: boolean;
  /** Which backend driver holds the harness tab's lease ('execution',
   *  'plans', 'guided-setup', 'teleport'), from the harness's init/status
   *  messages. While set the harness drops this panel's input. */
  leasedBy: string | null;
  /** Step-bound label for the "ZAC is driving" overlay ("Reading your app
   *  credentials…"). Null → the playful rotating fallback labels. */
  drivingStatus: string | null;
  /** A plan's run on another channel than the browser: the step ZAC is on, shown over the panel without blocking it. */
  runNote: string | null;
  latestFrame: string;
  fps: number;
  latency: number;
  viewport: { width: number; height: number };
  // Actions
  openPanel: () => void;
  closePanel: () => void;
  setConnected: (connected: boolean) => void;
  setHarnessConnected: (connected: boolean) => void;
  setZoomSignedIn: (signedIn: boolean) => void;
  setCurrentUrl: (url: string) => void;
  setPendingNavigateUrl: (url: string | null) => void;
  setDisabled: (disabled: boolean) => void;
  setAgentControl: (agentControl: boolean) => void;
  setUserPaused: (userPaused: boolean) => void;
  setLeasedBy: (leasedBy: string | null) => void;
  setDrivingStatus: (status: string | null) => void;
  setRunNote: (note: string | null) => void;
  setLatestFrame: (frame: string) => void;
  setStats: (fps: number, latency: number) => void;
  setViewport: (viewport: { width: number; height: number }) => void;
}

export const useBrowserStore = create<BrowserState>()((set) => ({
  panelOpen: false,
  connected: false,
  harnessConnected: false,
  zoomSignedIn: false,
  currentUrl: '',
  pendingNavigateUrl: null,
  disabled: false,
  agentControl: false,
  userPaused: false,
  leasedBy: null,
  drivingStatus: null,
  runNote: null,
  latestFrame: '',
  fps: 0,
  latency: 0,
  viewport: { width: 1280, height: 720 },

  openPanel: () => set({ panelOpen: true }),
  closePanel: () => set({ panelOpen: false }),
  setConnected: (connected) => set({ connected }),
  setHarnessConnected: (harnessConnected) => set({ harnessConnected }),
  setZoomSignedIn: (zoomSignedIn) => set({ zoomSignedIn }),
  setCurrentUrl: (currentUrl) => set({ currentUrl }),
  setPendingNavigateUrl: (pendingNavigateUrl) => set({ pendingNavigateUrl }),
  setDisabled: (disabled) => set({ disabled }),
  setAgentControl: (agentControl) => set({ agentControl }),
  setUserPaused: (userPaused) => set({ userPaused }),
  setLeasedBy: (leasedBy) => set({ leasedBy }),
  setDrivingStatus: (drivingStatus) => set({ drivingStatus }),
  setRunNote: (runNote) => set({ runNote }),
  setLatestFrame: (latestFrame) => set({ latestFrame }),
  setStats: (fps, latency) => set({ fps, latency }),
  setViewport: (viewport) => set({ viewport }),
}));
